import React, { useEffect, useRef, useState } from 'react';
import { Box, Button as MuiButton, CircularProgress, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../apiClient';
import { API_ENDPOINTS, ROUTES } from '../constants';
import useDocumentTitle from '../hooks/useDocumentTitle';

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 20; // ~1 minute

type PaymentState = 'checking' | 'failed' | 'timeout';

const PaymentStatusPage: React.FC = () => {
  useDocumentTitle('Payment status');
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<PaymentState>('checking');
  const pollCount = useRef(0);

  useEffect(() => {
    if (!orderId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const response = await apiClient.get(`${API_ENDPOINTS.PAYMENTS}/phonepe/status/${orderId}`);
        if (cancelled) return;

        if (response.data.paymentStatus === 'paid') {
          navigate(`${ROUTES.ORDER_CONFIRMATION}/${orderId}`, { replace: true });
          return;
        }
        if (response.data.paymentStatus === 'failed') {
          setState('failed');
          return;
        }
      } catch {
        // Transient error — keep polling until the limit
      }

      pollCount.current += 1;
      if (pollCount.current >= MAX_POLLS) {
        setState('timeout');
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orderId, navigate]);

  if (state === 'checking') {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <CircularProgress sx={{ mb: 3 }} />
        <Typography variant="h5" sx={{ mb: 1 }}>
          Confirming your payment…
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please don't close this page. This usually takes a few seconds.
        </Typography>
      </Box>
    );
  }

  const isTimeout = state === 'timeout';

  return (
    <Box sx={{ textAlign: 'center', py: 10 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {isTimeout ? 'Payment is still processing' : 'Payment failed'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
        {isTimeout
          ? 'We have not received a confirmation from PhonePe yet. Your order is saved — if the payment went through, it will be confirmed automatically. You can check the latest status on your order page.'
          : 'Your payment could not be completed. Your order is saved as unpaid — no money was captured. You can view the order in your account.'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
        <MuiButton variant="contained" component={RouterLink} to={`${ROUTES.ACCOUNT}/orders/${orderId}`}>
          View order
        </MuiButton>
        <MuiButton variant="outlined" component={RouterLink} to={ROUTES.PRODUCTS}>
          Continue shopping
        </MuiButton>
      </Box>
    </Box>
  );
};

export default PaymentStatusPage;
